export const MEXICO_CITY_TIME_ZONE = 'America/Mexico_City';

export type MexicoCityDateFormat =
  | 'yyyy-MM-dd'
  | 'dd-MM-yyyy'
  | 'yyyy/MM/dd'
  | 'dd/MM/yyyy'
  | 'dd/MMMM/yyyy'
  | 'dd/MMMM/yyyy HH:mm:ss'
  | 'HH:mm:ss'
  | 'EEEE'
  | 'yyyy';

const numericDateFormatter = new Intl.DateTimeFormat('es-MX', {
  timeZone: MEXICO_CITY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const longDateFormatter = new Intl.DateTimeFormat('es-MX', {
  timeZone: MEXICO_CITY_TIME_ZONE,
  year: 'numeric',
  month: 'long',
  day: '2-digit',
});

const timeFormatter = new Intl.DateTimeFormat('es-MX', {
  timeZone: MEXICO_CITY_TIME_ZONE,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

const weekdayFormatter = new Intl.DateTimeFormat('es-MX', {
  timeZone: MEXICO_CITY_TIME_ZONE,
  weekday: 'long',
});

const getPart = (
  parts: Intl.DateTimeFormatPart[],
  type: Intl.DateTimeFormatPartTypes
): string => parts.find((part) => part.type === type)?.value ?? '';

const assertValidDate = (date: Date): void => {
  if (Number.isNaN(date.getTime())) {
    throw new RangeError('Invalid date');
  }
};

export const formatMexicoCityDate = (
  date: Date,
  format: MexicoCityDateFormat
): string => {
  assertValidDate(date);

  if (format === 'EEEE') {
    return weekdayFormatter.format(date);
  }

  if (format === 'HH:mm:ss') {
    return timeFormatter.format(date);
  }

  const numericParts = numericDateFormatter.formatToParts(date);
  const year = getPart(numericParts, 'year');

  if (format === 'yyyy') {
    return year;
  }

  const day = getPart(numericParts, 'day');
  const month = getPart(numericParts, 'month');

  switch (format) {
    case 'yyyy-MM-dd':
      return `${year}-${month}-${day}`;
    case 'dd-MM-yyyy':
      return `${day}-${month}-${year}`;
    case 'yyyy/MM/dd':
      return `${year}/${month}/${day}`;
    case 'dd/MM/yyyy':
      return `${day}/${month}/${year}`;
    case 'dd/MMMM/yyyy':
    case 'dd/MMMM/yyyy HH:mm:ss': {
      const longDateParts = longDateFormatter.formatToParts(date);
      const longDate = `${getPart(longDateParts, 'day')}/${getPart(
        longDateParts,
        'month'
      )}/${getPart(longDateParts, 'year')}`;

      return format === 'dd/MMMM/yyyy HH:mm:ss'
        ? `${longDate} ${timeFormatter.format(date)}`
        : longDate;
    }
  }
};
